/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { Auth0ContextInterface } from '@auth0/auth0-react';
import { clearAuthenticationCache } from 'common/services/LogoutService';
import { configureSessionRedirect } from './AuthService';

let auth0LogoutFn: Auth0ContextInterface['logout'] | null = null;
let auth0ClientId: string | null = null;

/**
 * Sets the Auth0 logout function from the Auth0 context.
 * This should be called once at app startup from App.jsx to initialize the logout function.
 * @param logoutFn - The logout function from useAuth0()
 */
export function setAuth0Logout(logoutFn: Auth0ContextInterface['logout']) {
    auth0LogoutFn = logoutFn;
}

export function setAuth0ClientId(clientId: string) {
    auth0ClientId = clientId;
}

/**
 * Performs a full logout - clears local state and Auth0 session.
 * This should be used instead of the local logout() (which only focuses on cache cleanup) from AuthService when using Auth0 as the default auth method.
 * @param options - Optional configuration
 * @param options.returnTo - URL to redirect to after Auth0 logout
 * @param options.isAuthenticated - Whether the user is currently authenticated with Auth0
 * @param options.noRedirect - If true, will not redirect to previous path when you log in
 */
export function auth0Logout(options?: {
    returnTo?: string;
    isAuthenticated?: boolean;
    noRedirect?: boolean;
}) {
    if (globalThis.location.hash === '#/login') {
        // already on login page - no need to logout again
        return;
    }

    const redirectPath = options?.noRedirect ? '' : findRedirectPath();
    if (redirectPath) {
        configureSessionRedirect(redirectPath);
    }

    clearAuthenticationCache();

    const isAuth0Authenticated = options?.isAuthenticated ?? true;

    if (auth0LogoutFn && isAuth0Authenticated) {
        auth0LogoutFn!({
            clientId: auth0ClientId,
            logoutParams: {
                returnTo:
                    options?.returnTo ??
                    `${globalThis.location.origin}/#/login`,
            },
        });
    }
}

function findRedirectPath() {
    // check hash params for redirectTo (from AuthenticatedRouter redirect), or current path
    const hashQuery = globalThis.location.hash.split('?')[1] || '';
    const hashParams = new URLSearchParams(hashQuery);
    const redirect =
        hashParams.get('redirectTo') ||
        globalThis.location.hash.replace('#', '');
    return redirect;
}
